import type { RecordStepDirection, RecordStepperStore } from "~/components/recordStepper/recordStepperTypes";

import { Box, Icon, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import useRecordStepperView from "~/components/recordStepper/useRecordStepperView";
import { CaptionText } from "~/components/Typographies";

const STEP_ICON: Record<RecordStepDirection, string> = { next: "arrow_forward", previous: "arrow_back" };

const visuallyHiddenSx = {
	clip: "rect(0 0 0 0)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	whiteSpace: "nowrap",
	width: 1
} as const;

interface StepButtonProps {
	direction: RecordStepDirection;
	isEnabled: boolean;
	label: string;
	stepper: RecordStepperStore;
}

const StepButton = ({ direction, isEnabled, label, stepper }: StepButtonProps) => (
	<Tooltip title={label}>
		<span>
			<IconButton
				aria-label={label}
				disabled={!isEnabled}
				onClick={() => stepper.step(direction)}
				size="small"
				sx={{ p: 0.375 }}
			>
				<Icon sx={{ fontSize: "1.1rem" }}>{STEP_ICON[direction]}</Icon>
			</IconButton>
		</span>
	</Tooltip>
);

const RecordStepper = () => {
	const { t } = useTranslation("common");
	const { stepper, view } = useRecordStepperView();
	if (!stepper || !view.isVisible) return null;

	const { canNext, canPrevious, isStepping, position, total } = view;
	const counter =
		total === null
			? null
			: position === null
				? t("recordStepper.totalOnly", { total })
				: t("recordStepper.position", { position, total });

	return (
		<Box
			aria-busy={isStepping}
			aria-label={t("recordStepper.label")}
			role="group"
			sx={{
				alignItems: "center",
				border: 1,
				borderColor: "divider",
				borderRadius: 4,
				display: "flex",
				flexShrink: 0,
				gap: 0.25,
				pl: counter ? 1 : 0.25,
				pr: 0.25
			}}
		>
			{counter && (
				<CaptionText
					sx={{
						color: "text.secondary",
						fontVariantNumeric: "tabular-nums",
						opacity: isStepping ? 0.5 : 1,
						whiteSpace: "nowrap"
					}}
				>
					{counter}
				</CaptionText>
			)}
			<Box aria-live="polite" component="span" sx={visuallyHiddenSx}>
				{isStepping ? t("recordStepper.loading") : ""}
			</Box>
			<StepButton direction="previous" isEnabled={canPrevious} label={t("recordStepper.previous")} stepper={stepper} />
			<StepButton direction="next" isEnabled={canNext} label={t("recordStepper.next")} stepper={stepper} />
		</Box>
	);
};

export default RecordStepper;
